import {
  HTTP_INTERCEPTORS,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Area } from '../shared/enums/area.enum';
import { AuthService } from './services/auth.service';
import { ConfigurationService } from './services/configuration.service';
import { HttpHeaderService } from './services/http-header.service';
import { NotifyService } from './services/notify/notify.service';
import { LaboratoryHttpHeaderService } from './services/prothesis-api/laboratory-http-header.service';
import { TimeZoneSingleton } from './services/prothesis-api/time-zone-singleton';

/**
 * Opts a request out of the global notifications: the caller handles a failure itself (e.g. an asset preview left empty).
 * The login flow still starts on a 401.
 */
export const SKIP_API_NOTIFICATION = new HttpContextToken<boolean>(() => false);

// From https://medium.com/@MetonymyQT/angular-http-interceptors-what-are-they-and-how-to-use-them-52e060321088
@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private _apiHashCustomer: string | null | undefined;
  private _apiHashLaboratory: string | null | undefined;
  private _authService = inject(AuthService);
  private _configurationService = inject(ConfigurationService);
  private _customerApiAreaMessagePathRegex = /^.*api\/CustomerAreaMessage[/]{0,1}$/;
  private _getAllowedTenantsApiPathRegex = /^.*\/api\/authenticated\/User\/GetAllowedTenants[/]{0,1}$/;
  private _httpHeaderService = inject(HttpHeaderService);
  private _laboratoryApiAreaMessagePathRegex = /^.*api\/LaboratoryAreaMessage[/]{0,1}$/;
  private _laboratoryHttpHeaderService = inject(LaboratoryHttpHeaderService);
  private _notifyService = inject(NotifyService);
  private _router = inject(Router);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipApiNotificationFlag = request.context.get(SKIP_API_NOTIFICATION);
    if (
      !skipApiNotificationFlag &&
      this._isApiRequest(request.url) &&
      !this._isApiAreaMessageRequest(request.url) &&
      !this._isApiGetAllowedTenantsRequest(request.url)
    ) {
      this._notifyService.dismiss();
    }

    return next.handle(request).pipe(
      tap(
        (event) => {
          if (event instanceof HttpResponse) {
            if (!skipApiNotificationFlag && this._isApiRequest(event.url)) {
              this._notifyService.flagDown();
            }

            this._configureHttpHeaderData(event.headers);
            if (this._isProthesisApiRequest(event.url)) {
              this._configureLaboratoryHttpHeaderData(event.headers);
            }

            if (this._isGetInfoApiRequest(event.url)) {
              this._apiHashCustomer = event.headers.has('X-hash-customer') ? event.headers.get('X-hash-customer') : undefined;
              this._apiHashLaboratory = event.headers.has('X-hash-laboratory') ? event.headers.get('X-hash-laboratory') : undefined;
            } else if (this._isProthesisApiRequest(event.url) || this._isProthesisCustomerApiRequest(event.url)) {
              this._configureHash(event.headers, event.url);
            }
          }
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            this._configureHttpHeaderData(error.headers);
            if (this._isProthesisApiRequest(error.url)) {
              this._configureLaboratoryHttpHeaderData(error.headers);
            }

            if (skipApiNotificationFlag && error.status !== 401) {
              return;
            }

            if (error.status === 0) {
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow('Impossible de se connecter à Prothesis. Vérifier votre connexion internet.');
            } else if (error.status === -1) {
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow(
                'Vous devez choisir un laboratoire à explorer avant de pouvoir accéder à ses données.',
                () => {
                  this._router.navigateByUrl('/admin/laboratory');
                },
                'Laboratoires',
              );
            } else if (error.status === -2) {
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow(
                'Vous devez choisir un client à explorer avant de pouvoir accéder à ses données.',
                () => {
                  this._router.navigateByUrl('/customer');
                },
                'Clients',
              );
            } else if (error.status === 400) {
              const errorMessage = "Une erreur imprévue s'est produite.";
              const errorMessageFromApi = this._notifyService.buildErrorMessage(error);
              if (errorMessageFromApi.length === 0) {
                this._notifyService.flagDown();
                this._notifyService.displayErrorMessagesNow(errorMessage);
              } else {
                this._notifyService.displayErrorMessagesNow(errorMessageFromApi);
              }
            } else if (error.status === 404) {
              const errorMessage = "L'enregistrement auquel vous essayez d'accéder n'existe pas.";
              const errorMessageFromApi = this._notifyService.buildErrorMessage(error);
              if (errorMessageFromApi.length === 0) {
                this._notifyService.displayErrorMessagesNow(errorMessage);
              } else {
                this._notifyService.displayErrorMessagesNow(errorMessageFromApi);
              }
            } else if (error.status === 403) {
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow("Vous n'êtes pas autorisé à effectuer cette opération.");
            } else if (error.status === 401) {
              // Starts the oidc login flow
              this._authService.login();
            } else if (error.status === 409) {
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow(error.error);
            } else if (error.status === 503) {
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow("Votre requête n'a pas pu aboutir. Veuillez réessayer.");
            } else {
              /* For 500 errors and any unexpected HTTP error code */
              this._notifyService.flagDown();
              this._notifyService.displayErrorMessagesNow("Une erreur imprévue s'est produite.");
            }
          }
        },
      ),
    );
  }

  private _compareCustomerHash(apiHashCustomer, apiHashLaboratory): void {
    if (
      (apiHashCustomer && this._apiHashCustomer !== apiHashCustomer) ||
      (apiHashLaboratory && this._apiHashLaboratory !== apiHashLaboratory)
    ) {
      this._apiHashCustomer = apiHashCustomer;
      this._apiHashLaboratory = apiHashLaboratory;
      this._authService.getInfoToRefreshCustomer();
    }
  }

  private _compareLaboratoryHash(apiHashLaboratory): void {
    if (apiHashLaboratory && this._apiHashLaboratory !== apiHashLaboratory) {
      this._apiHashLaboratory = apiHashLaboratory;
      this._authService.getInfoToRefreshLaboratory();
    }
  }

  private _configureHash(headers: HttpHeaders, url: string | null): void {
    const apiHashCustomer = headers.has('X-hash-customer') ? headers.get('X-hash-customer') : undefined;
    const apiHashLaboratory = headers.has('X-hash-laboratory') ? headers.get('X-hash-laboratory') : undefined;

    if (this._authService.area !== 0 && !this._authService.area) {
      return;
    }
    if (this._authService.area === Area.Admin || this._authService.area === Area.Laboratory) {
      if (this._isProthesisApiRequest(url)) {
        this._compareLaboratoryHash(apiHashLaboratory);
      } else if (this._isProthesisCustomerApiRequest(url)) {
        this._compareLaboratoryHash(apiHashLaboratory);
        this._compareCustomerHash(apiHashCustomer, apiHashLaboratory);
      }
    }
    if (this._authService.area === Area.Customer) {
      this._compareCustomerHash(apiHashCustomer, apiHashLaboratory);
    }
  }

  private _configureHttpHeaderData(headers: HttpHeaders): void {
    if (headers !== null) {
      // If X-Customer-Area-Message-Update-Etag and X-Laboratory-Area-Message-Update-Etag are not set, this means we should remove the banner.
      // So we need to pass undefined to the setHttpHeaderInfo method bellow.
      // This http headers are working differently than the subscription, storage and suspended http headers.
      this._httpHeaderService.setHttpHeaderInfo(
        headers.has('X-Customer-Area-Message-Update-Etag') ? headers.get('X-Customer-Area-Message-Update-Etag') : undefined,
        headers.has('X-Laboratory-Area-Message-Update-Etag') ? headers.get('X-Laboratory-Area-Message-Update-Etag') : undefined,
      );
    }
  }

  private _configureLaboratoryHttpHeaderData(headers: HttpHeaders): void {
    if (TimeZoneSingleton.getInstance() && TimeZoneSingleton.getInstance().laboratoryTimeZone) {
      // If the subscription, storage and suspended http headers are not set, this means that the request is not a laboratory request
      // and we don't want to call the setHttpHeaderInfo as we don't have the information at this time for the subscription banner.
      // This http headers are working differently than the X-Customer-Area-Message-Update-Etag and X-Laboratory-Area-Message-Update-Etag.
      if (
        headers.has('X-Subscription-End-Date') &&
        headers.has('X-Last-Subscription-End-Date') &&
        headers.has('X-Last-Nonfree-Subscription-Product-Type') &&
        headers.has('X-Storage-Capacity-Limit') &&
        headers.has('X-Storage-Usage') &&
        headers.has('X-Suspended-Flag')
      ) {
        this._laboratoryHttpHeaderService.setHttpHeaderInfo(
          headers.get('X-Subscription-End-Date'),
          headers.get('X-Last-Subscription-End-Date'),
          headers.get('X-Last-Nonfree-Subscription-Product-Type'),
          headers.get('X-Storage-Capacity-Limit'),
          headers.get('X-Storage-Usage'),
          headers.get('X-Suspended-Flag'),
        );
      }
    }
  }

  private _isApiAreaMessageRequest(url: string): boolean {
    return this._laboratoryApiAreaMessagePathRegex.test(url) || this._customerApiAreaMessagePathRegex.test(url);
  }

  private _isApiGetAllowedTenantsRequest(url: string): boolean {
    return this._getAllowedTenantsApiPathRegex.test(url);
  }

  private _isApiRequest(url: string | null): boolean {
    return (
      url !== null &&
      (url.startsWith(this._configurationService.ProthesisApiAddress) ||
        url.startsWith(this._configurationService.ProthesisCustomerApiAddress) ||
        url.startsWith(this._configurationService.ProthesisIdentityApiAddress))
    );
  }

  private _isGetInfoApiRequest(url: string | null): boolean {
    return url !== null && url.indexOf('GetInfo') !== -1;
  }

  private _isProthesisApiRequest(url: string | null): boolean {
    return url !== null && url.startsWith(this._configurationService.ProthesisApiAddress);
  }

  private _isProthesisCustomerApiRequest(url: string | null): boolean {
    return url !== null && url.startsWith(this._configurationService.ProthesisCustomerApiAddress);
  }
}

export const apiInterceptorProvider = [{ provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true }];
